/* eslint max-lines: ["error", {max: 320}] -- Due to long file + Feature Flag is Temporal */
/* eslint complexity: ["error", {max: 17}] -- Due to long file + Feature Flag is Temporal */
import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";

import {
  ChannelClassification,
  ChannelTypes,
  GroupChannelId,
  GroupChannelPrefix,
  GroupChannelURLPrefixes,
  InformationalModalGroupChannelPrefixes,
  MentorRoleEvolutionChannelNames,
  SendbirdPushTestToolChannelUrlPrefix,
} from "@/constants/GroupChannels";
import type { GroupChannel, Member } from "@/crossPlatform/Sendbird";

// TODO:
// Some of this functions were copy - pasted from MyEquip web with minor modifications.
// Trying to keep them as similar as possible to avoid confusion in future updates
// or debugging.
//
// Ideally we should have a shared library for this kind of functions.
// Working on that.
// For now, this is the best approach.

const isIndividualChannel = (channelUrl: string): boolean => {
  if (isEmpty(channelUrl)) {
    return false;
  }

  return !GroupChannelURLPrefixes.some((prefix) =>
    channelUrl.startsWith(prefix),
  );
};

// sendbird creates a channel that only the current user can see
// this is used for testing purposes. We should not show this channel
const isPushTestToolChannel = (channelUrl: string): boolean => {
  return channelUrl.startsWith(SendbirdPushTestToolChannelUrlPrefix);
};

const isAdmissionsRelatedChannel = (channelUrl: string): boolean => {
  const { PatientToAdmissions, ProxyToAdmissions } = GroupChannelPrefix;

  if (isEmpty(channelUrl)) {
    return false;
  }

  return (
    channelUrl.startsWith(ProxyToAdmissions) ||
    channelUrl.startsWith(PatientToAdmissions)
  );
};

const isInformationalModalGroupChannel = (channelUrl: string): boolean => {
  if (isEmpty(channelUrl)) {
    return false;
  }

  return InformationalModalGroupChannelPrefixes.some((prefix) =>
    channelUrl.startsWith(prefix),
  );
};

const groupedChatChannelsByGroupType = (
  channels?: GroupChannel[],
): {
  admissionsChannels: GroupChannel[];
  directMessageChannels: GroupChannel[];
  groupChannels: GroupChannel[];
} => {
  const admissionsChannels: GroupChannel[] = [];
  const groupChannels: GroupChannel[] = [];
  const directMessageChannels: GroupChannel[] = [];

  channels?.forEach((channel) => {
    if (isPushTestToolChannel(channel.url)) {
      return;
    }
    if (
      isIndividualChannel(channel.url) &&
      !isAdmissionsRelatedChannel(channel.url)
    ) {
      directMessageChannels.push(channel);
    } else if (isInformationalModalGroupChannel(channel.url)) {
      groupChannels.push(channel);
    } else if (isAdmissionsRelatedChannel(channel.url)) {
      admissionsChannels.push(channel);
    }
  });

  return {
    admissionsChannels,
    directMessageChannels,
    groupChannels,
  };
};

const getInformationalModalGroupChannelDescription = ({
  channelUrl,
  isAppUserPatient,
  selectedPatientName,
}: {
  channelUrl: string;
  isAppUserPatient: boolean;
  selectedPatientName?: string;
}): string | undefined => {
  const {
    PatientCct,
    PatientProxyCct,
    ProxyCct,
    ProxyDietitian,
    ProxyFamily,
    ProxyMedical,
    ProxyPsych,
    ProxyTherapist,
  } = GroupChannelId;
  if (channelUrl.startsWith(ProxyCct) || channelUrl.startsWith(PatientCct)) {
    return "Your entire therapy, RD, coaching team";
  }
  if (channelUrl.startsWith(PatientProxyCct)) {
    if (isAppUserPatient) {
      return "Your entire therapy, RD, coaching team and supports";
    }
    return `Your entire therapy, RD, coaching team, ${selectedPatientName} and supports`;
  }
  if (!isAppUserPatient) {
    if (channelUrl.startsWith(ProxyFamily)) {
      return "All caregiver coaches and supports";
    }
    if (channelUrl.startsWith(ProxyTherapist)) {
      return "All therapists and supports";
    }
    if (channelUrl.startsWith(ProxyDietitian)) {
      return "All dietitians and supports";
    }
    if (channelUrl.startsWith(ProxyMedical)) {
      return "Your entire medical team and supports";
    }
    if (channelUrl.startsWith(ProxyPsych)) {
      return "Your entire psychiatry team and supports";
    }
  }

  return undefined;
};

const getMembersOfRole = ({
  members,
  role,
}: {
  members: Member[];
  role: SendbirdUserType;
}): Member[] => {
  return members.filter((member) => {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- Sendbird defines member.metaData as a generic 'object' type; our MemberMetaData type is more specific
    const memberMetaData = member.metaData as MemberMetaData;
    return memberMetaData.userType === role;
  });
};

const getChannelType = (channel: GroupChannel): ChannelTypes => {
  if (isNil(channel)) {
    return ChannelTypes.Unknown;
  }

  return (
    ChannelClassification.find((value) => channel.url.startsWith(value.url))
      ?.type ?? ChannelTypes.Unknown
  );
};

const getChannelDisplayName = ({
  channel,
  isAppUserPatient,
  selectedPatientName,
}: {
  channel: GroupChannel;
  isAppUserPatient: boolean;
  selectedPatientName?: string;
}) => {
  const channelType = getChannelType(channel);

  const displayChannelType = getMentorRoleEvolutionChannelType({
    channelType,
    channelUrl: channel.url,
  });

  // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
  switch (displayChannelType) {
    case ChannelTypes.PatientAndSupportsAndProviderTeam: {
      if (!isAppUserPatient && selectedPatientName) {
        return `${selectedPatientName} ${displayChannelType}`;
      }

      return ChannelTypes.SupportsAndProviderTeam;
    }

    case ChannelTypes.PatientAndSupportsTherapyRDCoachingTeam: {
      if (!isAppUserPatient && selectedPatientName) {
        return `${selectedPatientName} ${displayChannelType}`;
      }

      return ChannelTypes.ProxyTherapyRDCoachingTeam;
    }

    case ChannelTypes.PatientTherapyRDCoachingTeam: {
      if (isAppUserPatient) {
        return ChannelTypes.PatientTherapyRDCoachingTeam;
      }
      // Note: we want to use Supports + Therapy, RD, Coaching Team for the channel name
      // so the supports know they are in the right channel
      return ChannelTypes.ProxyTherapyRDCoachingTeam;
    }

    case ChannelTypes.ProviderTeam: {
      if (isAppUserPatient) {
        return ChannelTypes.ProviderTeam;
      }
      // Note: we want to use Supports + Provider Team for the channel name
      // so the supports know they are in the right channel
      return ChannelTypes.SupportsAndProviderTeam;
    }

    case ChannelTypes.ProxyTherapyRDCoachingTeam:
    case ChannelTypes.SupportsAndProviderTeam: {
      const providers = getMembersOfRole({
        members: channel.members,
        role: "Provider",
      });
      if (providers.length > 0) {
        return displayChannelType;
      }
      return ChannelTypes.Unknown;
    }

    default:
      return displayChannelType;
  }
};

interface GetMentorRoleEvolutionChannelTypeParams {
  channelType: ChannelTypes;
  channelUrl: string;
}
const getMentorRoleEvolutionChannelType = ({
  channelType,
  channelUrl,
}: GetMentorRoleEvolutionChannelTypeParams): ChannelTypes => {
  // Find which GroupChannelId this URL starts with
  const matchingChannelId = Object.values(GroupChannelId).find((id) =>
    channelUrl.startsWith(id),
  );

  if (!matchingChannelId) {
    return channelType;
  }

  return MentorRoleEvolutionChannelNames[matchingChannelId] ?? channelType;
};

const getProviderDisplayName = (providerType?: string): string | undefined => {
  switch (providerType) {
    case "Family Mentor":
      return "Caregiver Coach";

    case "Peer Mentor":
      return "Recovery Coach";

    case "Recovery Navigator":
      return "Care Navigator";

    case undefined:
      return undefined;

    default:
      return providerType;
  }
};

export {
  getChannelDisplayName,
  getInformationalModalGroupChannelDescription,
  getMembersOfRole,
  getProviderDisplayName,
  groupedChatChannelsByGroupType,
  isAdmissionsRelatedChannel,
  isIndividualChannel,
  isInformationalModalGroupChannel,
};
